// 介錯の踏み込みの「灰色の写し」(残像) を、足元の土ぼこりへ替える (2026-10-29・作者の答え exec-fx-q4)。
//
// 作者 (10-29): 灰色の写しは「コードで描いた感」。写しは消し、足元の土ぼこりにする。
// 11-01 から土ぼこりが配布版の既定。踏み込みの後に白い体の写しが残るのが「コードで描いた感」(11-01 の検収で撮って見つけた)。
//   前の版 (鋼色の写し) は DEV ?execStep=ghost。
// 踏み出し = 元の位置で後ろへ蹴り上げる・移動中 = 通り道にかけら・着地 = 左右へ広がる。
// 絵は exec_fx_mats.ts の dust (白＋アルファ。色はここで付ける)。描くのは render.ts の残像の枝。

import type { ExecutionerStepState, ExecutionerStepVisual } from './executioner_weapon'
import { drawMat, dustReady, dustSprite, preloadExecFxMats, tinted } from './exec_fx_mats'

export type ExecStepFxMode = 'dust' | 'ghost'

let mode: ExecStepFxMode = 'dust'
/** URL の値から切り替える。'ghost' = 前の版。それ以外 (指定なし) は 'dust' (配布版)。 */
export function setExecStepFx(value: string | null | undefined): ExecStepFxMode {
  mode = value === 'ghost' ? 'ghost' : 'dust'
  return mode
}
export function execStepFxMode(): ExecStepFxMode { return mode }

// 色は灰の城の床の土。明るい所だけ少し白く。
const DUST_RGB = '150,142,134'
const KICK_MS = 300
const LAND_MS = 380
const BIT_MS = 240
const BIT_U = [0.3, 0.55, 0.8]

const frameAt = (t: number): number => Math.min(3, Math.floor(t * 4))
// 出てすぐ濃く、後ろ半分でほどける。
const fadeAt = (t: number): number => (t < 0.15 ? t / 0.15 : 1) * (t > 0.5 ? 1 - (t - 0.5) / 0.5 : 1)

/** 土ぼこりで描くか。絵が読めていない間は false (呼ぶ側は何も描かない)。 */
export function execStepDustReady(): boolean {
  preloadExecFxMats()
  return dustReady()
}

/** 足元の土ぼこりを描く。h = ボスの表示の高さ (大きさの物差し)。 */
export function drawExecStepDust(
  ctx: CanvasRenderingContext2D, step: ExecutionerStepState, visual: ExecutionerStepVisual, footY: number, h: number,
): void {
  const age = visual.ageMs
  if (!(age >= 0) || !Number.isFinite(age)) return
  const dir = step.toX < step.fromX ? -1 : 1
  const back = step.kind === 'back'
  const size = h * (back ? 0.5 : 0.62)

  // 踏み出し: 元の位置で、進む向きの反対へ蹴り上げる (絵は右へ広がる向き)。
  const tk = age / KICK_MS
  if (tk < 1) {
    const img = dustSprite('kick', frameAt(tk))
    if (img) {
      const w = size * 1.15
      drawMat(ctx, tinted(img, DUST_RGB), step.fromX, footY, w, w * img.naturalHeight / img.naturalWidth,
        0.18, 0.98, 0.9 * fadeAt(tk), 0, dir > 0)
    }
  }
  // 移動中: 通り道の足元にかけら。
  for (let i = 0; i < BIT_U.length; i += 1) {
    const tb = (age - visual.travelMs * BIT_U[i]!) / BIT_MS
    if (tb < 0 || tb >= 1) continue
    const img = dustSprite('bit', i + (back ? 1 : 0))
    if (!img) continue
    const x = step.fromX + (step.toX - step.fromX) * BIT_U[i]!
    const w = size * 0.7
    drawMat(ctx, tinted(img, DUST_RGB), x, footY, w, w * img.naturalHeight / img.naturalWidth,
      0.5, 0.98, 0.7 * fadeAt(tb), 0, dir > 0)
  }
  // 着地: 止まった足元から左右へ。後退は着地を小さく。
  const tl = (age - visual.travelMs) / LAND_MS
  if (tl >= 0 && tl < 1) {
    const img = dustSprite('land', frameAt(tl))
    if (img) {
      const w = size * (back ? 1.0 : 1.35)
      drawMat(ctx, tinted(img, DUST_RGB), step.toX, footY, w, w * img.naturalHeight / img.naturalWidth,
        0.5, 0.98, 0.85 * fadeAt(tl), 0, dir < 0)
    }
  }
}
